import classNames from 'classnames';
import {action, computed, observable} from 'mobx';
import {observer, Provider} from 'mobx-react';
import {WithRouterProps} from 'next/dist/client/with-router';
import {withRouter} from 'next/router';
import React from 'react';

import {AsyncPriceCourseCardContext, BrowseCourse} from '@udemy/browse-course';
import {AGGREGATION_TYPE_PRICE} from '@udemy/browse-utils';
import {
    GwgCarousel,
    GwgAccelerator,
    AcademySelected,
    AcademyPresented,
} from '@udemy/career-academies';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {withMatchMedia} from '@udemy/hooks';
import {withI18n, WithI18nProps} from '@udemy/i18n';
import {MarketplaceOnly} from '@udemy/react-brand-components';
import {
    CertificationBundleUnit,
    CertificationExamVoucherBannerWrapper,
    VoucherUIRegion,
    CertificationVoucherBundleUnitCarousel,
    CertificationBundle,
    CertificationModel,
    CertificationUnit,
} from '@udemy/react-certification-preparation';
import {useExamByTopicId} from '@udemy/react-certification-vouchers';
import {CourseGuidanceTakeShortAssessmentEntrypoint} from '@udemy/react-course-highlights';
import {
    DiscoveryUnitsContainerStore,
    HorizontalBundleUnit,
    HorizontalBundleUnitProductCard,
    SkeletonCourseCarousel,
    SkeletonTitle,
    ValueProps,
} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent} from '@udemy/react-experimentation-provider';
import {EntryPointPopUp} from '@udemy/react-globalization-components/course-guidance';
import {ContentArea} from '@udemy/react-layout-components';
import {
    type RelatedCategoryItem,
    extractCategoryAndSubcategoryItems,
    fetchRecommendationSections,
    RecommendationContextIds,
    RecommendationSourceObjectTypes,
} from '@udemy/react-recommendations';
import {CONTEXT_TYPES, ResourceContextMenuProvider} from '@udemy/resource-context-menu';
import {udSentry} from '@udemy/sentry';
import {serverOrClient} from '@udemy/shared-utils';
import {
    SubscriptionPlanContext,
    SubscriptionPlanSubscriberState,
    ConsumerSubscriptionGwGStandalonePlan,
    ConsumerSubscriptionGwGPersonalPlan,
} from '@udemy/subscription-graphql';
import {CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, withUDData, WithUDDataProps} from '@udemy/ud-data';

import {isMwebStandardizedMobileCourseCardsEnabled} from 'src/lib/experimentation-helper';

import {LearnMoreSection} from './components/learn-more-section.react-component';
import {
    TopicDiscoveryListContainerWrapper,
    TopicDiscoveryUnitsContainerWrapper,
    TopicDiscoveryUnitsContainerWrapperLoader,
} from './components/topic-discovery-units-wrapper.react-component';
import {TopicEnrollmentStats} from './components/topic-enrollment-stats.react-component';
import {getHeaderVariant, TopicHeader} from './components/topic-header.react-component';
import {
    BUNDLE_DISCOUNT_CODE,
    CERT_HERO_ENROLLMENT_THRESHOLD,
    PRESET_ALL,
    PRESET_FREE,
    SPECIAL_PRICING_TOPIC_IDS_BY_LOCALE,
    SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
} from './constants';
import {TopicPageViewEvent} from './events';
import styles from './topic.module.less';
import {isOrgTopicPage, isSubsTopicPage} from './types/page';
import {HEADER_VARIANT, TopicContext} from './types/topic';
import {
    getLocalizedEnrollmentCount,
    getLocalizedEnrollmentStat,
    getPageObjectIdFromTopic,
    getPageType,
} from './utils';

export interface TopicPageExperimentVariants {
    /** Tapen `topic_subscription_upsell`: variant surfaces PP merchandising on topic discovery. */
    topicSubscriptionUpsellFeature?: FeatureVariantAssignment;
    /** TopicWrapper: mobile vertical / mweb standardized product-card layout for browse course cards */
    useVerticalRecoCourseCardStyle?: boolean;
    /**
     * True when mweb standardized mobile cards OR MX mobile vertical reco (Tapen) is on.
     * Mirrors category `threeCardVerticalLayoutExperimentIsEnabled` for discovery unit props / ContentArea.
     */
    threeCardVerticalLayoutExperimentIsEnabled?: boolean;
    isExperimentConfigsLoading?: boolean;
    showCodingExercisesBadge?: boolean;
    showUpdatedPPTopicPage?: boolean;
    showPersonalPlanBadge?: boolean;
    showCertificationBanner?: boolean;
    browseAggregatorPageEnabled?: boolean;
    showCourseGuidance?: boolean;
    fullQuickViewBoxFeature?: boolean;
    topicCatSubcatMwebCardStandardizationFeature?: FeatureVariantAssignment;
    showTopicSpecialBundlePricing?: FeatureVariantAssignment;
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    certificationVoucherBundleUnitFeature?: FeatureVariantAssignment;
    showCertificationTopicHeaderFeature?: FeatureVariantAssignment;
}

export interface TopicProps
    extends TopicContext,
        TopicPageExperimentVariants,
        WithUDDataProps,
        WithI18nProps {
    subsCollectionIds?: string;
    isConsumerSubsSubscriber: boolean;
    isSubsLoading?: boolean;
    // In the server side, withMatchMedia is returning null
    isMobile?: boolean | null;
    certification?: CertificationModel;
    certificationBundles: CertificationBundle[];
    isCertificationTopic?: boolean;
    genericTopicBundleCourses?: BrowseCourse[];
    enableLabsInPersonalPlan?: boolean;
    enableAssessmentsInPersonalPlan?: boolean;
    isLowSupplyTopicPage?: boolean;
    activeSubscriptionPlan?: SubscriptionPlanSubscriberState;
    gwgSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGStandalonePlan>;
    gwgPlusPPSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGPersonalPlan>;
    showGwgCarousel?: boolean;
    gwgCarouselAccelerator?: GwgAccelerator;
}

export interface CertificationSignalProps {
    hasVoucher: boolean;
    hasPracticeExam: boolean;
}

@withMatchMedia({isMobile: 'mobile-max'})
@observer
export class InternalTopic extends React.Component<
    TopicProps & WithRouterProps & CertificationSignalProps
> {
    private hasPublishedSpecialBundlePricingImpressionEvent = false;
    private hasPublishedCertificationTopicHeaderImpressionEvent = false;

    static defaultProps = {
        presetValue: PRESET_ALL,
        relatedPages: [],
        questionsAndAnswers: [],
        showPersonalPlanBadge: false,
        showCodingExercisesBadge: false,
        showUpdatedPPTopicPage: false,
        isMobile: false,
        enableLabsInPersonalPlan: false,
        enableAssessmentsInPersonalPlan: false,
        showCourseGuidance: false,
        fullQuickViewBoxFeature: false,
        isLowSupplyTopicPage: false,
        showGwgCarousel: false,
    };

    async componentDidMount() {
        this.fetchTopicContextData(this.props.topic.id);
        Tracker.publishEvent(
            new TopicPageViewEvent({
                topicId: getPageObjectIdFromTopic(this.props.topic),
            }),
        );
        this.updatePageContext();
        this.triggerGenericBundleImpressionEvent();
        this.triggerCertificationTopicHeaderImpressionEvent();
    }

    componentDidUpdate(prevProps: Readonly<TopicProps & WithRouterProps>) {
        if (
            prevProps.udData.Config.marketplace_country?.id !=
            this.props.udData.Config.marketplace_country?.id
        ) {
            this.updatePageContext();
        }
        this.triggerGenericBundleImpressionEvent();
        this.triggerCertificationTopicHeaderImpressionEvent();
    }

    private ref = React.createRef<HTMLDivElement>();
    @observable discoveryUnitsStore: DiscoveryUnitsContainerStore | undefined;
    @observable relatedCategories?: RelatedCategoryItem[] = undefined;

    @action
    private async fetchTopicContextData(topicId: string) {
        try {
            const contextualHeaderData = await fetchRecommendationSections({
                params: {
                    contextId: RecommendationContextIds.TOPIC_CONTEXTUAL_HEADER,
                    sourceObjects: [{type: RecommendationSourceObjectTypes.LABEL, id: topicId}],
                },
            });
            this.relatedCategories = extractCategoryAndSubcategoryItems(contextualHeaderData);
        } catch (e) {
            udSentry.captureException(e);
            this.relatedCategories = [];
        }
    }

    private resourceContextMenuProps = {
        context: CONTEXT_TYPES.ALL_COURSES,
    };

    updatePageContext = () => {
        const {udData, topic} = this.props;
        // This trigger braze, it gets the value of marketplace_country from udData.Config
        if (topic.id && udData.Config.marketplace_country?.id) {
            const updatedUDData = {
                marketplace_country: {},
                pageContext: {
                    entityId: topic.id.toString(),
                },
            };

            setTimeout(() => {
                window.dispatchEvent(
                    new CustomEvent(CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, {detail: updatedUDData}),
                );
            }, 0);
        }
    };

    triggerGenericBundleImpressionEvent = () => {
        const {genericTopicBundleCourses, showTopicSpecialBundlePricing, udData, topic} =
            this.props;

        if (this.hasPublishedSpecialBundlePricingImpressionEvent) {
            return;
        }

        if (genericTopicBundleCourses) {
            const locale = udData.request.locale;
            const specialPricingTopicIds = SPECIAL_PRICING_TOPIC_IDS_BY_LOCALE[locale];
            const topicId = Number(topic.id);

            if (specialPricingTopicIds) {
                const isSpecialPricingTopic = specialPricingTopicIds.includes(topicId);

                if (isSpecialPricingTopic && showTopicSpecialBundlePricing) {
                    Tracker.publishEvent(
                        new ExperimentImpressionEvent(
                            showTopicSpecialBundlePricing.experimentIds,
                            showTopicSpecialBundlePricing.isLocalDefault,
                        ),
                    );
                    this.hasPublishedSpecialBundlePricingImpressionEvent = true;
                }
            }
        }
    };

    triggerCertificationTopicHeaderImpressionEvent = () => {
        const {certDescription, showCertificationTopicHeaderFeature} = this.props;

        if (this.hasPublishedCertificationTopicHeaderImpressionEvent) {
            return;
        }

        if (certDescription && showCertificationTopicHeaderFeature) {
            Tracker.publishEvent(
                new ExperimentImpressionEvent(
                    showCertificationTopicHeaderFeature.experimentIds,
                    showCertificationTopicHeaderFeature.isLocalDefault,
                ),
            );
            this.hasPublishedCertificationTopicHeaderImpressionEvent = true;
        }
    };

    @computed
    get isMXUser() {
        const {udData, isConsumerSubsSubscriber} = this.props;
        const {Config} = udData;
        return !isConsumerSubsSubscriber && !Config.brand.has_organization;
    }

    @computed
    get isEnglishLocale() {
        const {udData} = this.props;
        const {request} = udData;
        return request.locale === 'en_US';
    }

    @computed
    get pageType() {
        return getPageType(this.props.udData, this.props.isConsumerSubsSubscriber);
    }

    @computed
    get arePageDataLoading() {
        return this.props.isSubsLoading || this.props.udData.isGlobalMeContextLoading;
    }

    @computed
    get headerVariant() {
        return getHeaderVariant({
            certification: this.props.certification,
            certDescription: this.props.certDescription,
            tertiaryDescription: this.props.tertiaryDescription,
            relatedCertificationBrowseAggregators: this.props.relatedCertificationBrowseAggregators,
            browseAggregatorPageEnabled: this.props.browseAggregatorPageEnabled,
            numCourses: this.props.numCourses,
            numExercises: this.props.numExercises,
            avgRating: this.props.avgRating,
            topicName: this.props.topic.name,
            topicImages: this.props.topic.images,
            showCertificationTopicHeaderFeature: this.props.showCertificationTopicHeaderFeature,
            isExperimentConfigsLoading: this.props.isExperimentConfigsLoading,
        });
    }

    @computed
    get hasReplacementTitle() {
        return this.headerVariant.type !== HEADER_VARIANT.DEFAULT;
    }

    get localizedEnrollmentStat() {
        return getLocalizedEnrollmentStat(this.props.enrollmentStat, this.props.locale);
    }

    get certHeroNumLearners() {
        return getLocalizedEnrollmentCount(
            this.props.numEnrollments,
            this.props.locale,
            CERT_HERO_ENROLLMENT_THRESHOLD,
        );
    }

    private renderDiscoveryUnitsLoader() {
        // TODO: Uncomment below when recommendations experiment is over
        // Slack with context: https://udemy.slack.com/archives/C1TGV7X6F/p1749567361558609
        //
        //const {isMobile, staticDiscoveryUnits} = this.props;
        //if (this.props.staticDiscoveryUnits?.length ?? 0 > 0) {
        //    return (
        //        <>
        //            <TopicDiscoveryUnitsContainerWrapper
        //                {...this.props}
        //                pageType={'topic'}
        //                disableInfiniteScroll={true}
        //                discoveryUnitFilter={this.discoveryUnitFilter}
        //                units={staticDiscoveryUnits}
        //                isStaticRender={serverOrClient.isServer}
        //            />
        //            <SkeletonCourseList courseCount={4} />
        //            <SkeletonCourseList courseCount={4} />
        //        </>
        //    );
        //} else {
        //    return <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!isMobile} />;
        //}
        const {isMobile} = this.props;
        return <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!isMobile} />;
    }

    private renderCourseGuidanceBanner() {
        if (this.props.showCourseGuidance) {
            return (
                <div className="component-margin" data-item-index={3}>
                    <CourseGuidanceTakeShortAssessmentEntrypoint />
                </div>
            );
        }
        return <></>;
    }

    private renderStaticUnits() {
        return (
            <>
                {this.props.showGwgCarousel && this.props.gwgCarouselAccelerator && (
                    <div className={classNames(styles['gwg-unit-wrapper'], 'component-margin')}>
                        <div className={classNames('ud-heading-xl', styles['gwg-unit-header'])}>
                            {gettext('Learn AI with Google')}
                        </div>
                        <TrackImpression
                            trackFunc={() => {
                                Tracker.publishEvent(
                                    new AcademyPresented({
                                        academySlug: this.props.gwgCarouselAccelerator?.slug ?? '',
                                        uiRegion: 'learn-ai-with-google',
                                    }),
                                );
                            }}
                        >
                            <div>
                                <GwgCarousel
                                    accelerator={this.props.gwgCarouselAccelerator}
                                    currentlySubscribedPlan={this.props.activeSubscriptionPlan}
                                    onEnrollmentClick={() => {
                                        Tracker.publishEvent(
                                            new AcademySelected({
                                                academySlug:
                                                    this.props.gwgCarouselAccelerator?.slug ?? '',
                                                uiRegion: 'learn-ai-with-google',
                                            }),
                                        );
                                    }}
                                />
                            </div>
                        </TrackImpression>
                    </div>
                )}
                {this.renderBundleUnit()}
            </>
        );
    }

    private renderBundleUnit() {
        const {udData, isConsumerSubsSubscriber, certificationBundles} = this.props;
        const {Config} = udData;

        const isMXUser = !isConsumerSubsSubscriber && !Config.brand.has_organization;

        // Generic bundles are only available for MX users
        const showGenericBundles = isMXUser;
        const showCertificationBundle = isMXUser && certificationBundles.length > 0;

        const showSpecialBundlePricingExperimentEnabled =
            this.props.showTopicSpecialBundlePricing?.configuration?.enableSpecialBundlePricing ??
            false;
        const showCertificationVoucherBundleUnit =
            this.props.certificationVoucherBundleUnitFeature?.configuration
                ?.showCertificationVoucherBundleUnit ?? false;

        // Cert bundle discount experiment props
        // These will be provided to the bundle unit only for cert bundles
        const specialBundleDiscountProps = {
            isBundleDiscountEnabled: true,
            bundleDiscountCode: BUNDLE_DISCOUNT_CODE,
        };

        const voucherContainerClass = classNames(styles['voucher-banner-bundle-unit-container'], {
            [styles['voucher-above']]:
                this.props.isTopicVoucherModuleAboveBundle?.configuration
                    ?.isVoucherModuleAbovebundle ?? false,
            [styles['voucher-below']]: !(
                this.props.isTopicVoucherModuleAboveBundle?.configuration
                    ?.isVoucherModuleAbovebundle ?? false
            ),
        });

        if (showCertificationBundle) {
            // Voucher banner for English locale: render voucher banner following certification bundle unit rules
            // This shows the voucher banner only in English when certification bundle unit is present
            // Voucher banner positioning: determines if voucher banner renders above or below certification bundle unit
            // based on the experiment configuration (isTopicVoucherModuleAboveBundle)
            return (
                <div className={classNames(voucherContainerClass, 'component-margin')}>
                    {showCertificationVoucherBundleUnit ? (
                        <TrackImpression
                            visibilityThreshold={0}
                            trackFunc={() => {
                                if (this.props.certificationVoucherBundleUnitFeature) {
                                    const {experimentIds = [], isLocalDefault = false} =
                                        this.props.certificationVoucherBundleUnitFeature;
                                    Tracker.publishEvent(
                                        new ExperimentImpressionEvent(
                                            experimentIds,
                                            isLocalDefault,
                                        ),
                                    );
                                }
                            }}
                        >
                            <FunnelLogContextProvider pageType="topic">
                                <>
                                    <CertificationVoucherBundleUnitCarousel
                                        title={gettext('Get Certified with Top Instructors')}
                                        subtitle={gettext(
                                            'Explore curated bundles from expert instructors. Browse to compare teaching styles and prep strategies.',
                                        )}
                                        units={this.props.certificationBundles.map((bundle) => ({
                                            bundle: bundle,
                                            showCommonInstructor: true,
                                            ...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
                                        }))}
                                    />
                                </>
                            </FunnelLogContextProvider>
                        </TrackImpression>
                    ) : (
                        <TrackImpression
                            visibilityThreshold={0}
                            trackFunc={() => {
                                if (this.props.certificationVoucherBundleUnitFeature) {
                                    const {experimentIds = [], isLocalDefault = false} =
                                        this.props.certificationVoucherBundleUnitFeature;
                                    Tracker.publishEvent(
                                        new ExperimentImpressionEvent(
                                            experimentIds,
                                            isLocalDefault,
                                        ),
                                    );
                                }
                            }}
                        >
                            <div
                                className={classNames(styles['certification-bundle-unit-wrapper'])}
                                data-item-index={0}
                            >
                                <CertificationBundleUnit
                                    certificationBundle={this.props.certificationBundles[0]}
                                    {...specialBundleDiscountProps}
                                />
                            </div>
                        </TrackImpression>
                    )}
                    <CertificationExamVoucherBannerWrapper
                        topicId={this.props.topic.id}
                        theme="dark"
                        layout="horizontal"
                        isTopicBanner
                        experimentData={this.props.isTopicVoucherModuleAboveBundle}
                        uiRegion={
                            this.props.isTopicVoucherModuleAboveBundle?.configuration
                                ?.isTopicVoucherModuleAbovebundle
                                ? ('above_course_bundle' as VoucherUIRegion)
                                : ('below_course_bundle' as VoucherUIRegion)
                        }
                        showAddToCart
                    />
                </div>
            );
        } else if (showGenericBundles && this.props.genericTopicBundleCourses) {
            if (this.props.isMobile && this.props.isExperimentConfigsLoading) {
                return (
                    <>
                        <div
                            className={classNames(
                                styles['certification-bundle-unit-wrapper'],
                                'component-margin',
                            )}
                            data-item-index={0}
                            data-purpose="generic-bundle-experiment-pending"
                            data-testid="generic-bundle-experiment-pending"
                        >
                            <SkeletonTitle className={styles['title-skeleton']} />
                            <SkeletonCourseCarousel courseCount={3} />
                        </div>
                        <div className={styles['voucher-banner-generic-bundle-unit']}>
                            <CertificationExamVoucherBannerWrapper
                                topicId={this.props.topic.id}
                                theme="dark"
                                layout="horizontal"
                                isTopicBanner
                                experimentData={this.props.isTopicVoucherModuleAboveBundle}
                                uiRegion={
                                    this.props.isTopicVoucherModuleAboveBundle?.configuration
                                        ?.isTopicVoucherModuleAbovebundle
                                        ? ('above_course_bundle' as VoucherUIRegion)
                                        : ('below_course_bundle' as VoucherUIRegion)
                                }
                                showAddToCart
                            />
                        </div>
                    </>
                );
            }
            const mwebStandardizedLargeBundleCards =
                !!this.props.isMobile &&
                isMwebStandardizedMobileCourseCardsEnabled(
                    this.props.topicCatSubcatMwebCardStandardizationFeature?.configuration,
                );
            const useLargeProductCardBundle =
                this.props.useVerticalRecoCourseCardStyle === true ||
                mwebStandardizedLargeBundleCards;
            const genericBundleTitle = interpolate(
                gettext("Looking to advance your skills in %(topicName)s? We've got you."),
                {topicName: this.props.topic.name},
                true,
            );
            const genericBundleSubtitle = gettext(
                'Get everything you need to reach your goals in one convenient bundle.',
            );
            const genericBundleValueProps = [
                gettext('Top-rated courses'),
                gettext('Popular with learners just like you'),
                gettext('Guidance from real-world experts'),
            ];
            const genericBundleDiscountSpread = showSpecialBundlePricingExperimentEnabled
                ? specialBundleDiscountProps
                : {};

            return (
                <>
                    <div
                        className={classNames(
                            styles['certification-bundle-unit-wrapper'],
                            'component-margin',
                        )}
                        data-item-index={0}
                    >
                        {useLargeProductCardBundle ? (
                            <ContentArea>
                                <HorizontalBundleUnitProductCard
                                    stackedCourseCardLayout
                                    contents={this.props.genericTopicBundleCourses}
                                    title={genericBundleTitle}
                                    subtitle={genericBundleSubtitle}
                                    valueProps={genericBundleValueProps}
                                    courseCardProps={{isAsyncStaticPrice: true}}
                                    {...genericBundleDiscountSpread}
                                />
                            </ContentArea>
                        ) : (
                            <HorizontalBundleUnit
                                contents={this.props.genericTopicBundleCourses}
                                title={genericBundleTitle}
                                subtitle={genericBundleSubtitle}
                                valueProps={genericBundleValueProps}
                                {...genericBundleDiscountSpread}
                            />
                        )}
                    </div>

                    {/* Voucher banner for generic bundles: render voucher banner below generic bundle unit */}
                    <div className={styles['voucher-banner-generic-bundle-unit']}>
                        <CertificationExamVoucherBannerWrapper
                            topicId={this.props.topic.id}
                            theme="dark"
                            layout="horizontal"
                            isTopicBanner
                            experimentData={this.props.isTopicVoucherModuleAboveBundle}
                            uiRegion={
                                this.props.isTopicVoucherModuleAboveBundle?.configuration
                                    ?.isTopicVoucherModuleAbovebundle
                                    ? ('above_course_bundle' as VoucherUIRegion)
                                    : ('below_course_bundle' as VoucherUIRegion)
                            }
                            showAddToCart
                        />
                    </div>
                </>
            );
        } else if (isMXUser) {
            // Voucher banner for non-English locales: render voucher banner independently from certification bundle unit
            // This allows the voucher banner to show in all languages for MX users, not just English
            return (
                <div className={voucherContainerClass}>
                    <CertificationExamVoucherBannerWrapper
                        topicId={this.props.topic.id}
                        theme="dark"
                        layout="horizontal"
                        isTopicBanner={true}
                        experimentData={this.props.isTopicVoucherModuleAboveBundle}
                        // Send this uiRegion even when there's no certification bundle unit was approved by Anh Dao
                        uiRegion={
                            this.props.isTopicVoucherModuleAboveBundle?.configuration
                                ?.isTopicVoucherModuleAbovebundle
                                ? ('above_course_bundle' as VoucherUIRegion)
                                : ('below_course_bundle' as VoucherUIRegion)
                        }
                        showAddToCart
                    />
                </div>
            );
        } else {
            return null;
        }
    }

    get topicEnrollmentStatContent() {
        return !!this.localizedEnrollmentStat ? (
            <TopicEnrollmentStats
                data-item-index={0}
                enrollmentStat={this.localizedEnrollmentStat}
            />
        ) : null;
    }

    get certificationUnitContent() {
        const {topic} = this.props;

        const showCertificationUnit =
            isSubsTopicPage(this.pageType) || isOrgTopicPage(this.pageType);

        return showCertificationUnit ? (
            <CertificationUnit
                data-item-index={0}
                className="component-margin"
                topicId={topic.id}
            />
        ) : null;
    }

    get discoveryListContainerContent() {
        const ref = this.ref;

        const handlePageChange = () => {
            if (serverOrClient.global.scrollY && ref?.current) {
                const y = ref?.current?.getBoundingClientRect().top + serverOrClient.global.scrollY;
                serverOrClient.global.scrollTo?.({
                    top: y,
                    behavior: 'smooth',
                });
            }
        };

        const presetFilters =
            this.props.presetValue === PRESET_FREE
                ? {[AGGREGATION_TYPE_PRICE]: ['price-free']}
                : undefined;

        return (
            <div className="component-margin" data-item-index={-2} ref={this.ref}>
                <TopicDiscoveryListContainerWrapper
                    {...this.props}
                    pageType={this.pageType}
                    presetFilters={presetFilters}
                    handlePageChange={handlePageChange}
                />
            </div>
        );
    }

    get learnMoreSectionContent() {
        const {
            presetValue,
            primaryDescription,
            questionsAndAnswers,
            relatedPages,
            topic,
            isMobile,
            gettext,
            interpolate,
        } = this.props;

        const relatedPagesHeadline = {
            title: interpolate(
                gettext('Free %(topicName)s lessons'),
                {topicName: topic.name},
                true,
            ),
            secondaryText: gettext('Bite-sized learning in minutes'),
            className: styles['section-container'],
            layoutVariant: isMobile ? 'compact' : 'default',
        };

        return (
            <div className={styles['learn-more-section-container']}>
                <LearnMoreSection
                    className={styles['learn-more-section']}
                    topic={topic}
                    primaryDescription={primaryDescription}
                    questionsAndAnswers={questionsAndAnswers}
                    relatedPagesHeadline={{
                        ...relatedPagesHeadline,
                    }}
                    relatedPages={relatedPages}
                    presetValue={presetValue}
                    isMobile={!!isMobile}
                />
            </div>
        );
    }

    discoveryUnitFilter = (unit: Partial<DiscoveryUnit>) => {
        const {presetValue, enableLabsInPersonalPlan, enableAssessmentsInPersonalPlan} = this.props;
        const {Config, me} = this.props.udData;
        const isUBUser = me.is_authenticated && !!me.organization;
        const isUBProUser =
            isUBUser &&
            (Config.features.organization.learning_path.pro_path ||
                (me.is_authenticated && me.organization?.is_pro_license_holder));

        const shouldShowLabs =
            (isSubsTopicPage(this.pageType) && enableLabsInPersonalPlan) || isUBProUser;
        const shouldShowAssessments =
            (isSubsTopicPage(this.pageType) && enableAssessmentsInPersonalPlan) || isUBProUser;

        if (presetValue === PRESET_FREE) {
            return unit.type === 'bestseller_labels';
        }

        /**
         * Currently, if we're using certification banner, we're already showing the related_categories_and_subcategories
         * results inside of it, so we need to filter out from the discovery units container
         * ˝
         * TODO: remove this condition after the related categories and subcategories are removed from the DiscoveryAPI
         */
        if (
            unit.type === 'related_categories_and_subcategories' ||
            unit.type === 'category' ||
            unit.type === 'subcategory'
        ) {
            return false;
        }

        if (!shouldShowLabs) {
            return unit?.item_type !== 'lab';
        }
        if (!shouldShowAssessments) {
            return unit?.item_type !== 'assessment';
        }

        // no filter
        return true;
    };

    @observable private discoveryUnitsStorePageType?: string;
    @observable private discoveryUnitsStorePageObjectId?: number;

    @action
    getOrInitializeDiscoveryUnitStore = (initializationParam?: {
        pageType: string;
        pageObjectId: number;
    }) => {
        if (!initializationParam) {
            return undefined;
        }

        const {pageType, pageObjectId} = initializationParam;

        const storeMatchesPage =
            this.discoveryUnitsStore &&
            this.discoveryUnitsStorePageType === pageType &&
            this.discoveryUnitsStorePageObjectId === pageObjectId;

        if (storeMatchesPage) {
            return this.discoveryUnitsStore;
        }

        this.discoveryUnitsStore = new DiscoveryUnitsContainerStore({pageType, pageObjectId});
        this.discoveryUnitsStorePageType = pageType;
        this.discoveryUnitsStorePageObjectId = pageObjectId;

        if (!this.discoveryUnitsStore.loading && this.discoveryUnitsStore.firstLoad) {
            this.discoveryUnitsStore.fetchUnits({pageSize: 50});
        }

        return this.discoveryUnitsStore;
    };

    render() {
        const {primaryDescription, questionsAndAnswers, relatedPages, topic} = this.props;

        const showLearnMoreSection =
            primaryDescription || relatedPages.length > 1 || questionsAndAnswers.length > 0;

        const pageObjectId = getPageObjectIdFromTopic(topic);

        const {isConsumerSubsSubscriber, showPersonalPlanBadge, showCodingExercisesBadge} =
            this.props;

        /**
         * Wait for page data to be loaded before initializing discovery store since it depends on pageType and
         * pageType depends on page data (subs and uddata)
         */
        if (!this.arePageDataLoading) {
            this.getOrInitializeDiscoveryUnitStore({
                pageType: this.pageType,
                pageObjectId,
            });
        }

        const isDiscoveryUnitsLoading =
            this.arePageDataLoading || this.getOrInitializeDiscoveryUnitStore()?.firstLoad;

        /**
         * Provider of Mobx requires immutable props, in order to avoid below problem, don't forget to wait for
         * page data to be loaded or just don't wrap with it until the page data is loaded.
         * "MobX Provider: The set of provided stores has changed." error in loading stage.
         * https://unpkg.com/browse/mobx-react@7.2.1/src/Provider.tsx
         */
        const mobxProviderProps = {
            isConsumerSubsSubscriber,
            showPersonalPlanBadge,
            showCodingExercisesBadge,
        };
        const threeCardVerticalLayoutExperimentIsEnabled =
            this.props.threeCardVerticalLayoutExperimentIsEnabled ??
            isMwebStandardizedMobileCourseCardsEnabled(
                this.props.topicCatSubcatMwebCardStandardizationFeature?.configuration,
            );
        const wrapTopicDiscoveryInContentArea =
            !!this.props.isMobile && threeCardVerticalLayoutExperimentIsEnabled;
        /**
         * TopicWrapper already wraps legacy Topic with AsyncPriceCourseCardContext for vertical reco.
         * These inner providers historically used value={{}}, which overrides the parent and strips
         * useCourseProductCard — stacked / single-column cards stay condensed (browse-course).
         * Keep the same value TopicWrapper passes via useVerticalRecoCourseCardStyle.
         */
        const verticalRecoCourseCardContextValue =
            this.props.useVerticalRecoCourseCardStyle === true
                ? {useCourseProductCard: true, hideCourseDescription: true}
                : {};
        const topicDiscoverySection = (
            <TopicDiscoveryUnitsContainerWrapper
                {...this.props}
                store={this.getOrInitializeDiscoveryUnitStore({
                    pageType: this.pageType,
                    pageObjectId,
                })}
                pageType={this.pageType}
                discoveryUnitFilter={this.discoveryUnitFilter}
                ppMerchandisingVariant={this.props.isCertificationTopic ? 'cert' : 'general'}
                fetchOptions={{
                    /**
                     * This is a temporary solution for handling the infinite scroll with the
                     * children that injected from outside. So rather than using the infinite scroll
                     * and fetching more units after the last unit, we're just fetching the units
                     * all together and showing them in the page.
                     *
                     * Problem in here is that, we're injecting a unit from outside to as last unit,
                     * and we're trying to fetch a new unit in the middle of the page.
                     */
                    pageSize: 50,
                }}
            >
                {!this.hasReplacementTitle && this.topicEnrollmentStatContent}
                {this.certificationUnitContent}
                {this.discoveryListContainerContent}
                <ValueProps className="component-margin" />
            </TopicDiscoveryUnitsContainerWrapper>
        );
        return (
            <>
                <TopicHeader
                    avgRating={this.props.avgRating}
                    browseAggregatorPageEnabled={this.props.browseAggregatorPageEnabled}
                    certification={this.props.certification}
                    certDescription={this.props.certDescription}
                    hasVoucher={this.props.hasVoucher}
                    hasPracticeExam={this.props.hasPracticeExam}
                    isLoading={this.arePageDataLoading}
                    isExperimentConfigsLoading={this.props.isExperimentConfigsLoading}
                    isMobile={!!this.props.isMobile}
                    localizedEnrollmentStat={this.localizedEnrollmentStat}
                    certHeroNumLearners={this.certHeroNumLearners}
                    numCourses={this.props.numCourses}
                    numExercises={this.props.numExercises}
                    pageType={this.pageType}
                    presetValue={this.props.presetValue}
                    relatedCategories={this.relatedCategories}
                    relatedCertificationBrowseAggregators={
                        this.props.relatedCertificationBrowseAggregators
                    }
                    showCertificationTopicHeaderFeature={
                        this.props.showCertificationTopicHeaderFeature
                    }
                    tertiaryDescription={this.props.tertiaryDescription}
                    topicName={this.props.topic.name}
                    topicTitle={this.props.topicTitle}
                    topicImages={this.props.topic.images}
                />
                <div className="ud-container ud-page-wrapper">
                    <AsyncPriceCourseCardContext.Provider
                        value={verticalRecoCourseCardContextValue}
                    >
                        {this.renderStaticUnits()}
                    </AsyncPriceCourseCardContext.Provider>
                    {this.renderCourseGuidanceBanner()}
                    <EntryPointPopUp />
                    {!isDiscoveryUnitsLoading ? (
                        <Provider {...mobxProviderProps}>
                            <ResourceContextMenuProvider
                                resourceContextMenuProps={this.resourceContextMenuProps}
                            >
                                <AsyncPriceCourseCardContext.Provider
                                    value={verticalRecoCourseCardContextValue}
                                >
                                    {wrapTopicDiscoveryInContentArea ? (
                                        <ContentArea>{topicDiscoverySection}</ContentArea>
                                    ) : (
                                        topicDiscoverySection
                                    )}
                                </AsyncPriceCourseCardContext.Provider>
                            </ResourceContextMenuProvider>
                        </Provider>
                    ) : (
                        this.renderDiscoveryUnitsLoader()
                    )}

                    {showLearnMoreSection && (
                        <MarketplaceOnly>{this.learnMoreSectionContent}</MarketplaceOnly>
                    )}
                </div>
            </>
        );
    }
}

/**
 * Resolves the two certification signals and injects them as props, so the class component can
 * consume them via `this.props` without calling hooks itself.
 *
 * - `hasVoucher`: derived from `useExamByTopicId`, the same source the voucher banner uses.
 * - `hasPracticeExam`: derived synchronously from the certification bundles already in props
 *   (true when any bundled course is a practice test course).
 */
function withCertificationSignals<
    P extends {topic: TopicProps['topic']; certificationBundles: CertificationBundle[]},
>(WrappedComponent: React.ComponentType<P & CertificationSignalProps>): React.FC<P> {
    const WithCertificationSignals: React.FC<P> = (props) => {
        const {voucherPool, examId, isAvailableForRegion} = useExamByTopicId(props.topic.id);
        const hasVoucher =
            !!voucherPool && !!examId && isAvailableForRegion && voucherPool.hasAvailableVouchers;

        const hasPracticeExam = (props.certificationBundles ?? []).some((bundle) =>
            (bundle.contents ?? []).some(
                (course) => (course as BrowseCourse).is_practice_test_course === true,
            ),
        );

        return (
            <WrappedComponent
                {...props}
                hasVoucher={hasVoucher}
                hasPracticeExam={hasPracticeExam}
            />
        );
    };
    WithCertificationSignals.displayName = 'WithCertificationSignals';
    return WithCertificationSignals;
}

export const Topic = withI18n(withUDData(withRouter(withCertificationSignals(InternalTopic))));
